import { SmartRenderer } from '@fusioncharts/core/src/component-interface';
import { deg2Rad, polarToCartesian } from '@fusioncharts/utils/src/scale-utils/polar-util.js';
import { toRaphaelColor, isIpad, BLANKSTRING, toPrecision, rgbaToHex, parseTooltext } from '@fusioncharts/core/src/lib';
import { M, A, C, HALF_PI, PI, PI2, SMALLSQUARE, MEDIUMSQUARE } from '../../chart/chord/utils';

const normalizeAngles = radians => radians - HALF_PI,
  ribbonGenerator = (points, radius, startAngle) => {
    const corners = points.map((p, i) => {
      let angle = deg2Rad(p),
        { x, y } = polarToCartesian(radius, normalizeAngles(deg2Rad(startAngle)) + angle),
        curveFactor = 0.6,
        distance = Math.abs(deg2Rad(points[3 - i]) - angle),
        nextPointDist = Math.abs(deg2Rad(points[(i + 1) % 4]) - angle);
      if (distance > PI) {
        distance = PI2 - distance;
      }
      if (distance * radius < radius) {
        curveFactor *= distance;
      }

      x = toPrecision(x, 4);
      y = toPrecision(y, 4);

      return {
        x: x,
        y: y,
        cpX: (1 - curveFactor) * x,
        cpY: (1 - curveFactor) * y,
        arc: nextPointDist
      };
    });
    return [
      M,
      corners[0].x, corners[0].y,
      A, radius, radius, 0, corners[0].arc > PI ? 1 : 0, 1, corners[1].x, corners[1].y,
      C, corners[1].cpX, corners[1].cpY, corners[2].cpX, corners[2].cpY, corners[2].x, corners[2].y,
      A, radius, radius, 0, corners[2].arc > PI ? 1 : 0, 1, corners[3].x, corners[3].y,
      C, corners[3].cpX, corners[3].cpY, corners[0].cpX, corners[0].cpY, corners[0].x, corners[0].y
    ].join(` `);
  },
  mouseOverFn = function () {
    const node = this,
      chart = node.getFromEnv('chart');
    this.getLinkedParent().linkHoverIn(this.config.key);
    let eventArgs = {};
    setEventArgs(eventArgs, node);
    chart.fireChartInstanceEvent('linkRollOver', eventArgs);
  },
  mouseOutFn = function () {
    const node = this,
      chart = node.getFromEnv('chart');
    this.getLinkedParent().linkHoverOut(this.config.key);
    let eventArgs = {};
    setEventArgs(eventArgs, node);
    chart.fireChartInstanceEvent('linkRollOut', eventArgs);
  },
  mouseClickFn = function () {
    const node = this,
      chart = node.getFromEnv('chart');
    let eventArgs = {};
    setEventArgs(eventArgs, node);
    chart.fireChartInstanceEvent('linkClick', eventArgs);
  },
  setEventArgs = function (eventArgs, node) {
    // RED-8927: Support for plot click,label click and link click  events for Chord Diagram
    // sets the Arguments for mouse click/over/out events.
    eventArgs.dominantFlowValue = node.config[node.config.dominantNode[0]];
    let i;
    // RED-8927stores the index of subservient node.
    for (i = 0; i < node.config.linkedNodes.length; i++) {
      if (node.config.linkedNodes[i] !== node.config.dominantNode[0]) {
        break;
      }
    }

    eventArgs.subservientFlowValue = node.config[node.config.linkedNodes[i]];
    eventArgs.linkedNodes = node.config.linkedNodes;
    if (eventArgs.dominantFlowValue === eventArgs.subservientFlowValue) {
      eventArgs.color = node.config.rawColor || rgbaToHex(node.config.normalState.fill);
    } else {
      eventArgs.color = rgbaToHex(node.config.normalState.fill);
    }
    eventArgs.alpha = node.config.alpha;
  },
  getUniqueNodes = (nodeArr) => {
    let resultingNodes = [];
    if (nodeArr.length === 0) {
      return [];
    } else if (nodeArr.length === 1) {
      return ([nodeArr[0]]);
    }
    resultingNodes = nodeArr.filter((c, index) => {
      return nodeArr.indexOf(c) === index;
    });
    return (resultingNodes);
  },
  getDominantFlowValue = (_config, _nodeArr) => {
    let i,
      config = _config,
      nodeArr = _nodeArr,
      resultValues = [];
    if (!nodeArr.length) {
      return [];
    }
    if (nodeArr.length === 1) {
      return config[nodeArr[0]];
    }
    for (i = 0; i < nodeArr.length; i++) {
      resultValues.push(config[nodeArr[i]]);
    }
    return resultValues;
  },
  getPlotIdentifier = (colorArr, uniChar, legendItem, finalPath) => {
    let body = BLANKSTRING,
      graphicalElement,
      symbolBBox,
      i;
    if (finalPath !== '' && legendItem && legendItem.length) {
      if (legendItem.length === 1) {
        graphicalElement = legendItem[0].getGraphicalElement();
        symbolBBox = graphicalElement.legendItemSymbol.node.getBBox();
        return (`<svg height= ${symbolBBox.height} width = ${symbolBBox.width} overflow='visible' xmlns="http://www.w3.org/2000/svg">
        <path d= ${finalPath} fill = ${graphicalElement.legendItemSymbol.node.getAttribute('fill')} stroke = ${graphicalElement.legendItemSymbol.node.getAttribute('stroke')}
        stroke-opacity = ${graphicalElement.legendItemSymbol.node.getAttribute('stroke-opacity')} fill-opacity = ${graphicalElement.legendItemSymbol.node.getAttribute('fill-opacity')} 
        stroke-width = ${graphicalElement.legendItemSymbol.node.getAttribute('stroke-width')} stroke-linecap = ${graphicalElement.legendItemSymbol.node.getAttribute('stroke-linecap')}></path>
      </svg>`);
      }
      for (i = 0; i < legendItem.length; i++) {
        graphicalElement = legendItem[i].getGraphicalElement();
        symbolBBox = graphicalElement.legendItemSymbol.node.getBBox();
        body += `<svg height= ${symbolBBox.height} width = ${symbolBBox.width} overflow='visible' xmlns="http://www.w3.org/2000/svg">
          <path d= ${finalPath} fill = ${graphicalElement.legendItemSymbol.node.getAttribute('fill')} stroke = ${graphicalElement.legendItemSymbol.node.getAttribute('stroke')}
          stroke-opacity = ${graphicalElement.legendItemSymbol.node.getAttribute('stroke-opacity')} fill-opacity = ${graphicalElement.legendItemSymbol.node.getAttribute('fill-opacity')} 
          stroke-width = ${graphicalElement.legendItemSymbol.node.getAttribute('stroke-width')} stroke-linecap = ${graphicalElement.legendItemSymbol.node.getAttribute('stroke-linecap')}></path>
        </svg>`;
      }
    } else if (colorArr.length) {
      for (i = 0; i < colorArr.length; i++) {
        body += `
            <span style='color: ${colorArr[i]};'>
              ${uniChar}
            </span>`;
      }
    }
    return body;
  },
  getLegendItems = (legend, manager, dominantNodes) => {
    let legendItems = [],
      i;
    if (dominantNodes && dominantNodes.length) {
      if (dominantNodes.length === 1) {
        return ([legend.getItem(manager.config.legendItemMap[dominantNodes[0]])]);
      }
      for (i = 0; i < dominantNodes.length; i++) {
        legendItems.push(legend.getItem(manager.config.legendItemMap[dominantNodes[i]]));
      }
    }
    return legendItems;
  },
  getTooltext = (config, chartConfig, basefontsize, ribbon) => {
    let uniChar = isIpad ? SMALLSQUARE : MEDIUMSQUARE,
      key,
      body = BLANKSTRING,
      drawCustomLegendIcon = chartConfig && chartConfig.drawcustomlegendicon,
      macroIndices = [135, 136, 137, 138, 139, 140, 146, 147],
      manager = ribbon.getLinkedParent(),
      legend = ribbon.getFromEnv('legend'),
      pathArr,
      newPathArr = [],
      graphicalElement,
      legendItemSymbol,
      i,
      j,
      symbolBBox,
      pathStart = [],
      finalPath,
      dominantLegendItem = (drawCustomLegendIcon && legend && legend.config && legend.config.isActive) ? getLegendItems(legend, manager, getUniqueNodes(config.dominantNode)) : null,
      subservientLegendItem = (drawCustomLegendIcon && legend && legend.config && legend.config.isActive) ? getLegendItems(legend, manager, getUniqueNodes(config.subservientNode)) : null,
      parserConfig,
      linkToolText;
    finalPath = BLANKSTRING;
    if (dominantLegendItem && dominantLegendItem.length) {
      graphicalElement = dominantLegendItem && dominantLegendItem[0] && dominantLegendItem[0].getGraphicalElement();
      legendItemSymbol = graphicalElement && graphicalElement.legendItemSymbol;
      pathArr = legendItemSymbol && legendItemSymbol.attrs.path.split(/(?=[LMCA])/);
      symbolBBox = legendItemSymbol && legendItemSymbol.node.getBBox();
      if (pathArr && pathArr.length) {
        for (i = 0; i < pathArr.length; i++) {
          pathStart.push(pathArr[i][0]);
          pathArr[i] = pathArr[i].substring(1, pathArr[i].length);
          if (i === (pathArr.length - 1)) {
            pathArr[i] = pathArr[i].substring(0, pathArr[i].length - 1);
          }
          newPathArr[i] = pathArr[i].split(',');
        }
      }
      if (newPathArr && newPathArr.length) {
        for (i = 0; i < newPathArr.length; i++) {
          if (newPathArr[i].length) {
            for (j = 0; j < newPathArr[i].length; j++) {
              newPathArr[i][j] = Number(newPathArr[i][j]);
              if (pathStart[i] !== 'A') {
                if (j === 0) {
                  newPathArr[i][j] = newPathArr[i][j] - (symbolBBox.x);
                } else if (j === 1) {
                  newPathArr[i][j] = newPathArr[i][j] - (symbolBBox.y);
                }
              } else if (pathStart[i] === 'A') {
                if (j === 5) {
                  newPathArr[i][j] = newPathArr[i][j] - (symbolBBox.x);
                } else if (j === 6) {
                  newPathArr[i][j] = newPathArr[i][j] - (symbolBBox.y);
                }
              }
            }
          }
          finalPath += pathStart[i] + newPathArr[i].toString();
        }
      }
      finalPath = finalPath + 'Z';
    }
    parserConfig = {
      toolText: config.tooltext,
      dominantNode: getUniqueNodes(config.dominantNode),
      subservientNode: getUniqueNodes(config.subservientNode),
      dominantFlowDataValue: getUniqueNodes(config.dominantFlowDataValue),
      subservientFlowDataValue: getUniqueNodes(config.subservientFlowDataValue)
    };
    ;
    parserConfig.dominantFlowValue = getDominantFlowValue(config, parserConfig.dominantNode);
    parserConfig.subservientFlowValue = getDominantFlowValue(config, parserConfig.subservientNode);
    parserConfig.dominantPlotIdentifier = getPlotIdentifier(getUniqueNodes(config.dominantNodeColor), uniChar, dominantLegendItem, finalPath);
    parserConfig.subservientPlotIdentifier = getPlotIdentifier(getUniqueNodes(config.subservientNodeColor), uniChar, subservientLegendItem, finalPath);

    linkToolText = parseTooltext(config.tooltext, macroIndices,
      parserConfig, config);
    config.setLinkToolText = linkToolText;

    for (key in config.tooltip) {
      if (config.tooltip.hasOwnProperty(key)) {
        body += `
          <span style='color: ${config.tooltip[key].color};'>
            ${uniChar}
          </span>
          ${key + config.toolTipSepChar}
          ${config.showLinkValueOnHover ? ` &nbsp;` : ''}
          ${config.showLinkValueOnHover ? config.tooltip[key].value : ''}
        <br>`;
      }
    }

    if (!config.tooltext) {
      return (
        `<div style='padding: 2px; vertical-align: middle; font-size: ${basefontsize}px;'>
          ${body}
        </div>`
      );
    }
    return (
      `<div style='padding: 2px; vertical-align: middle; font-size: ${basefontsize}px;'>
        ${config.setLinkToolText}
      </div>`
    );
  };
class Ribbon extends SmartRenderer {
  constructor (id) {
    super(id);
    this.addEventListener('fc-mouseover', mouseOverFn);
    this.addEventListener('fc-mouseout', mouseOutFn);
    this.addEventListener('fc-click', mouseClickFn);
  }
  configureAttributes (dataObj) {
    super.configureAttributes(dataObj);
    Object.assign(this.config, dataObj);
  }

  setDimension (dim) {
    this.config.radius = dim.radius;
  }

  allocatePosition () {
    let config = this.config;
    config.path = ribbonGenerator(config.points, config.radius, 0);
    config.ribbonAttrs = config.hovered ? config.focussedState : config.unfocussed ? config.unfocussedState : config.normalState;
    config.ribbonAttrs.path = config.path;
    config.ribbonAttrs['stroke-width'] = (config.showBorder ? config.borderThickness : 0) || 0;
  }

  draw () {
    const ribbon = this,
      chartConfig = ribbon.getFromEnv('chart').config,
      height = chartConfig.canvasHeight,
      width = chartConfig.canvasWidth,
      cx = chartConfig.canvasLeft + width / 2,
      cy = chartConfig.canvasTop + height / 2,
      chartWidth = chartConfig.width,
      chartHeight = chartConfig.height,
      ribbonRadius = chartConfig.ribbonRadius,
      config = ribbon.config,
      angles = config.angles,
      tooltext = config.showToolTip ? getTooltext(config, chartConfig, chartConfig.style.baseFontSize, ribbon) : BLANKSTRING,
      { x: x1, y: y1 } = polarToCartesian(ribbonRadius, normalizeAngles(deg2Rad(angles[0]))),
      { x: x2, y: y2 } = polarToCartesian(ribbonRadius, normalizeAngles(deg2Rad(angles[3])));
    if (config.dominantNode.length > 1 && config.rawColor) {
      config.ribbonAttrs.fill = toRaphaelColor({
        color: `${config.rawColor[0]},${config.rawColor[1]}`,
        alpha: config.hovered ? config.hoverAlpha : config.unfocussed ? config.unfocussedAlpha : config.alpha,
        x1: (x1 + cx) / chartWidth,
        y1: (y1 + cy) / chartHeight,
        x2: (x2 + cx) / chartWidth,
        y2: (y2 + cy) / chartHeight
      });
      config.ribbonAttrs.stroke = toRaphaelColor({
        color: `${config.rawColor[0]},${config.rawColor[1]}`,
        alpha: config.hovered ? config.hoveredAlpha : config.unfocussed ? config.unfocussedAlpha : config.borderAlpha,
        x1: (x1 + cx) / chartWidth,
        y1: (y1 + cy) / chartHeight,
        x2: (x2 + cx) / chartWidth,
        y2: (y2 + cy) / chartHeight
      });
    }
    ribbon.addGraphicalElement({
      el: 'path',
      label: 'ribbon',
      tooltext,
      attr: config.ribbonAttrs,
      container: {
        id: 'link-container',
        isParent: true
      },
      component: ribbon
    });
  }

  getName () {
    return 'ribbon';
  }

  getType () {
    return 'dataset';
  }
}

export default Ribbon;
